import type { OpeningHour, Address } from '../../../services/stores/stores-sdk'
import { capitalizeFirstLetter } from '../../../utils/string'
import { formatTime } from './utils'
import type {
  StoreLDJsonParams,
  OpeningHoursSpecification,
  StoreLDJson
} from './types'

const groupOpeningHoursByTime = (
  openingHours: OpeningHour[]
): Map<
  string,
  { openTime: string | null; closeTime: string | null; days: OpeningHour[] }
> => {
  const timeGroupMap = new Map<
    string,
    { openTime: string | null; closeTime: string | null; days: OpeningHour[] }
  >()

  openingHours.forEach((day) => {
    const openTime = day.openingTime ? formatTime(day.openingTime) : null
    const closeTime = day.closingTime ? formatTime(day.closingTime) : null
    const isClosed = day.isClosed || !openTime || !closeTime

    if (isClosed) {
      return
    }

    const key = `${openTime}-${closeTime}`

    if (!timeGroupMap.has(key)) {
      timeGroupMap.set(key, {
        openTime,
        closeTime,
        days: []
      })
    }

    const group = timeGroupMap.get(key)
    if (group) {
      group.days.push(day)
    }
  })

  return timeGroupMap
}

const generateOpeningHoursSpecification = (
  openingHours?: OpeningHour[]
): OpeningHoursSpecification[] | undefined => {
  if (!openingHours || openingHours.length === 0) {
    return undefined
  }

  const timeGroupMap = groupOpeningHoursByTime(openingHours)
  const specifications: OpeningHoursSpecification[] = []

  Array.from(timeGroupMap.values()).forEach((group) => {
    if (group.days.length === 0 || !group.openTime || !group.closeTime) {
      return
    }

    const dayNames = group.days.map((day) =>
      capitalizeFirstLetter(day.dayOfWeek.name?.toLowerCase())
    )

    specifications.push({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: dayNames.length === 1 ? dayNames[0] : dayNames,
      opens: group.openTime,
      closes: group.closeTime
    })
  })

  return specifications.length > 0 ? specifications : undefined
}

const generateAddressAndGeo = (address?: Address) => {
  if (!address) {
    return { postalAddress: undefined, geoCoordinates: undefined }
  }

  const postalAddress =
    address.addressLine1 || address.city || address.postalCode
      ? {
          '@type': 'PostalAddress',
          ...(address.addressLine1 && { streetAddress: address.addressLine1 }),
          ...(address.city && { addressLocality: address.city }),
          ...(address.postalCode && { postalCode: address.postalCode }),
          ...(address.countryCode && { addressCountry: address.countryCode })
        }
      : undefined

  const { latitude, longitude } = address.location ?? {}
  const geoCoordinates =
    latitude !== undefined && longitude !== undefined
      ? {
          '@type': 'GeoCoordinates',
          latitude,
          longitude
        }
      : undefined

  return { postalAddress, geoCoordinates }
}

export const generateStoreLDJson = (params: StoreLDJsonParams): StoreLDJson => {
  const {
    storeName,
    storeUrl,
    telephone,
    storeImage,
    description,
    address,
    openingHours
  } = params

  const { postalAddress, geoCoordinates } = generateAddressAndGeo(address)
  const openingHoursSpec = generateOpeningHoursSpecification(openingHours)

  return {
    '@context': 'https://schema.org',
    '@type': 'Store',
    '@id': storeUrl,
    name: storeName,
    url: storeUrl,
    description,
    ...(telephone && { telephone }),
    ...(storeImage && { image: [storeImage] }),
    ...(postalAddress && { address: postalAddress }),
    ...(geoCoordinates && { geo: geoCoordinates }),
    ...(openingHoursSpec && { openingHoursSpecification: openingHoursSpec })
  }
}
